import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const EnrolledCourseContentPage = () => {
  const INITIAL_SECTIONS_TO_SHOW = 6;
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [visibleSectionsCount, setVisibleSectionsCount] = useState(INITIAL_SECTIONS_TO_SHOW);
  const [loadStep, setLoadStep] = useState(6);

  useEffect(() => {
    loadCourseWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const handlePopState = () => {
      navigate('/dashboard', { replace: true });
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  const loadCourseWorkspace = async () => {
    setLoading(true);
    setError('');

    try {
      const courseId = parseInt(id, 10);
      if (Number.isNaN(courseId)) {
        setError('Invalid course ID.');
        setLoading(false);
        return;
      }

      const [enrollmentsResponse, courseResponse, sectionsResponse] = await Promise.all([
        api.get('/api/user-dashboard/courses'),
        api.get(`/api/courses/${courseId}`),
        api.get(`/api/course-sections/course/${courseId}`)
      ]);

      const enrolledCourses = enrollmentsResponse?.data?.enrollments || [];
      const isEnrolled = enrolledCourses.some((enrollment) => enrollment?.course?.id === courseId);

      if (!isEnrolled) {
        setError('You are not enrolled in this course.');
        setLoading(false);
        return;
      }

      setCourse(courseResponse.data);

      const sections = sectionsResponse.data || [];
      const sectionData = await Promise.all(
        sections.map(async (section) => {
          const [contentsRes, questionsRes, liveSessionRes] = await Promise.allSettled([
            api.get(`/api/course-contents/section/${section.id}`),
            api.get(`/api/course-questions/section/${section.id}`),
            api.get(`/api/live-sessions/section/${section.id}`)
          ]);

          const contents = contentsRes.status === 'fulfilled' ? (contentsRes.value.data || []) : [];
          const questions = questionsRes.status === 'fulfilled' ? (questionsRes.value.data || []) : [];
          const liveSession = liveSessionRes.status === 'fulfilled' ? liveSessionRes.value.data : null;

          const recordings = [];
          const notes = [];
          const assignments = [];

          contents.forEach((content) => {
            if (content.contentType === 'VIDEO') {
              recordings.push({
                id: `video-${content.id}`,
                title: content.title,
                description: content.description || '',
                url: content.videoUrl,
                source: 'Course Video'
              });
            }

            if (content.contentType === 'DOCUMENT') {
              notes.push({
                id: `doc-${content.id}`,
                title: content.title,
                description: content.description || '',
                url: content.documentUrl
              });
            }

            if (content.contentType === 'QUESTION' && content.question) {
              assignments.push({
                id: `content-question-${content.id}`,
                title: content.question.title || content.title,
                description: content.question.description || content.description || '',
                questionId: content.question.id
              });
            }
          });

          questions.forEach((question) => {
            assignments.push({
              id: `section-question-${question.id}`,
              title: question.title,
              description: question.description || '',
              questionId: question.id
            });
          });

          if (liveSession?.recordingUrl) {
            recordings.push({
              id: `live-recording-${liveSession.id}`,
              title: liveSession.title || `${section.title} Recording`,
              description: liveSession.description || '',
              url: liveSession.recordingUrl,
              source: 'Live Session Recording'
            });
          }

          const dedupeById = (items) => {
            const seen = new Set();
            return items.filter((item) => {
              if (seen.has(item.id)) return false;
              seen.add(item.id);
              return true;
            });
          };

          return {
            id: section.id,
            title: section.title,
            description: section.description || '',
            recordings: dedupeById(recordings),
            notes: dedupeById(notes),
            assignments: dedupeById(assignments)
          };
        })
      );
      setSections(sectionData);
      setVisibleSectionsCount(INITIAL_SECTIONS_TO_SHOW);
    } catch (err) {
      setError('Failed to load course workspace.');
      console.error('Enrolled course workspace error:', err);
    }

    setLoading(false);
  };

  const filteredSections = useMemo(() => sections, [sections]);
  const visibleSections = useMemo(
    () => filteredSections.slice(0, visibleSectionsCount),
    [filteredSections, visibleSectionsCount]
  );
  const hasMoreSections = visibleSectionsCount < filteredSections.length;

  const totals = useMemo(() => sections.reduce((acc, section) => ({
    recordings: acc.recordings + section.recordings.length,
    notes: acc.notes + section.notes.length,
    assignments: acc.assignments + section.assignments.length
  }), { recordings: 0, notes: 0, assignments: 0 }), [sections]);

  const totalItems = totals.recordings + totals.notes + totals.assignments;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 pt-20 pb-6">
      <div className="max-w-6xl mx-auto px-4">
        {loading && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
            <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white/80">Building your learning arena...</p>
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-950/40 border border-red-500/40 rounded-2xl p-8 text-center">
            <p className="text-red-300 font-medium">{error}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="rounded-3xl p-[1px] bg-gradient-to-r from-fuchsia-500 via-blue-500 to-emerald-500 mb-6">
              <div className="bg-slate-900 rounded-3xl p-6 sm:p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="text-center md:text-left">
                    <p className="text-white/60 text-sm mb-2">Your Learning Space</p>
                    <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{course?.title || 'Course Workspace'}</h1>
                    <p className="text-white/70">
                      Interactive resources to keep momentum high and learning fun.
                    </p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4 min-w-64">
                    <p className="text-white/70 text-sm mb-2">Learning activity</p>
                    <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-pink-500 to-violet-500" style={{ width: `${Math.min(100, Math.round((totals.recordings / Math.max(1, totalItems)) * 100))}%` }} />
                    </div>
                    <p className="text-xs text-white/60 mt-2">
                      {sections.length} sections • {totalItems} resources
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {filteredSections.length === 0 ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
                <p className="text-white/70">No sections found.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {visibleSections.map((section, index) => {
                  return (
                    <div key={section.id} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex-1 text-left">
                          <p className="text-white/60 text-xs mb-1">Section {index + 1}</p>
                          <h3 className="text-base font-semibold text-white">{section.title}</h3>
                          {section.description && <p className="text-xs text-white/70 mt-1">{section.description}</p>}
                          <div className="flex items-center gap-2 flex-wrap mt-3">
                            <span className="px-2 py-1 text-xs rounded-full bg-pink-500/20 text-pink-200 border border-pink-400/30">🎬 {section.recordings.length}</span>
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-500/20 text-blue-200 border border-blue-400/30">📝 {section.notes.length}</span>
                            <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">🚀 {section.assignments.length}</span>
                          </div>
                        </div>
                        <div className="sm:flex-shrink-0">
                          <button
                            onClick={() => navigate(`/dashboard/courses/${id}/sections/${section.id}`)}
                            className="px-3 py-1.5 text-xs rounded-lg bg-indigo-500/30 border border-indigo-300/40 text-indigo-100 hover:bg-indigo-500/40"
                          >
                            Open Learning View →
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {hasMoreSections && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <p className="text-white/70 text-sm">
                      Showing {visibleSections.length} of {filteredSections.length} sections
                    </p>
                    <div className="flex items-center gap-2">
                      <select
                        value={loadStep}
                        onChange={(e) => setLoadStep(parseInt(e.target.value, 10))}
                        className="px-2 py-1.5 text-xs rounded-lg bg-slate-800 border border-white/20 text-white"
                      >
                        <option value={3}>Load 3 more</option>
                        <option value={6}>Load 6 more</option>
                        <option value={10}>Load 10 more</option>
                      </select>
                      <button
                        onClick={() => setVisibleSectionsCount((prev) => Math.min(prev + loadStep, filteredSections.length))}
                        className="px-3 py-1.5 text-xs rounded-lg bg-indigo-500/30 border border-indigo-300/40 text-indigo-100 hover:bg-indigo-500/40"
                      >
                        Load More Sections
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <footer className="mt-8 mb-2 border-t border-white/10 pt-4 text-center">
              <p className="text-white/50 text-xs">Keep learning every day. Small progress, big results.</p>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export default EnrolledCourseContentPage;
